import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Building2,
  ChevronDown,
  FileMinus,
  FilePlus,
  Info,
  CalendarClock,
  Loader2,
  Mail,
  Send,
  XCircle,
} from "lucide-react";
import {
  isBaseInvoiceType,
  isExportInvoiceType,
  isConsumerFinalReceiver,
  isCreditNote,
  isDebitNote,
} from "@fiscalo/shared-types";

import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { FinancialSummary } from "@/components/shared/FinancialSummary";
import { InvoiceDetailHeader } from "@/components/shared/InvoiceDetailHeader";
import { InvoiceSendSettingsCard } from "@/components/shared/InvoiceSendSettingsCard";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { RelatedDocumentsList } from "@/components/shared/RelatedDocumentsList";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  invoiceCreationBlockedTooltipKey,
  useInvoiceCreationBlocked,
} from "@/hooks/useInvoiceCreationBlocked";
import { useReissueRejectedInvoice } from "@/hooks/useReissueRejectedInvoice";
import { formatDisplayDate, toUruguayDateTimeParts } from "@/lib/dateFormat";
import { invoiceKeys, recurringInvoiceKeys } from "@/lib/queryKeys";
import { resolveConsumerFinalName } from "@/pages/invoices/invoice-list-shared";
import {
  confirmRecurringOccurrence,
  getPendingRecurringForInvoice,
} from "@/services/recurringInvoiceService";
import { getInvoice, downloadInvoicePdf, downloadDeelInvoicePdf, downloadInvoiceXml, sendInvoiceEmail, sendDraftInvoice, deleteDraftInvoice, dismissInvoiceRejection, scheduleInvoiceSend, cancelScheduledInvoiceSend } from "@/services/invoiceService";
import type { InvoiceDetailEnriched } from "@/services/invoiceService";
import { ScheduleSendDialog } from "@/components/features/invoices/ScheduleSendDialog";

/** DGI statuses where submission is still in progress; keep polling GET detail. */
const PENDING_DGI_STATUSES = ["pending", "sent"] as const;

function isDgiPending(dgiStatus: string | undefined): boolean {
  return (PENDING_DGI_STATUSES as readonly string[]).includes(dgiStatus ?? "pending");
}

/**
 * Poll while this document or any related NC/ND still has pending DGI status.
 */
function shouldPollInvoiceDetail(inv: InvoiceDetailEnriched | undefined): boolean {
  if (!inv) return false;
  const invDgiStatus = (inv as unknown as Record<string, unknown>).dgiStatus as string | undefined;
  if (isDgiPending(invDgiStatus)) return true;
  const related = [
    ...(inv.relatedCreditNotes ?? []),
    ...(inv.relatedDebitNotes ?? []),
  ];
  return related.some((n) => {
    const nDgi = (n as unknown as Record<string, unknown>).dgiStatus as string | undefined;
    return isDgiPending(nDgi);
  });
}

function isAcceptedBaseWithBalance(inv: InvoiceDetailEnriched): boolean {
  const dgiSt = (inv as unknown as Record<string, unknown>).dgiStatus as string | undefined;
  return (
    inv.status === "sent" &&
    dgiSt === "confirmed" &&
    typeof inv.remainingBalance === "number" &&
    typeof inv.originalInvoiceTotal === "number"
  );
}

function isAcceptedBase(cfeType: string, status: string, dgiStatus: string | undefined): boolean {
  return status === "sent" && dgiStatus === "confirmed" && isBaseInvoiceType(cfeType);
}

function formatMoney(currency: string, amount: number): string {
  return `${currency} ${Number(amount).toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Maps DGI rejection code or message to a friendly i18n key under invoices.detail.rejectionReason.* */
function rejectionReasonKey(reason: { code?: string; message?: string } | null): string | null {
  if (!reason) return null;
  const code = (reason.code ?? "").toLowerCase();
  const msg = (reason.message ?? "").toLowerCase();
  if (
    code.includes("receptor") ||
    code.includes("rut") ||
    msg.includes("receptor") ||
    msg.includes("rut") ||
    msg.includes("receiver")
  )
    return "receiverInvalid";
  if (code.includes("cae") && (code.includes("exhaust") || msg.includes("agotad")))
    return "caeExhausted";
  if (code.includes("valid") || msg.includes("validación") || msg.includes("validation"))
    return "validation";
  return null;
}

export function InvoiceDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [pdfLoading, setPdfLoading] = useState(false);
  const [deelPdfLoading, setDeelPdfLoading] = useState(false);
  const [xmlLoading, setXmlLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [sendingDraft, setSendingDraft] = useState(false);
  const [deletingDraft, setDeletingDraft] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [cancellingSchedule, setCancellingSchedule] = useState(false);
  const [cancelScheduleConfirmOpen, setCancelScheduleConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [technicalOpen, setTechnicalOpen] = useState(true);
  const [reissueConfirmOpen, setReissueConfirmOpen] = useState(false);
  const previousInvoiceStatusRef = useRef<string | undefined>(undefined);
  /** Set when a draft is deleted so the refetch-after-invalidate doesn't surface a load error toast. */
  const draftDeletedRef = useRef(false);
  const { reissue: reissueRejected } = useReissueRejectedInvoice();

  const { blocked: invoicingBlocked, reason: invoicingBlockedReason } =
    useInvoiceCreationBlocked();
  const invoicingBlockedTooltipKey =
    invoiceCreationBlockedTooltipKey(invoicingBlockedReason);

  const { data: invoice, isLoading, isError } = useQuery({
    queryKey: invoiceKeys.detail(id ?? ""),
    queryFn: () => getInvoice(id!),
    enabled: Boolean(id),
    /** Global default staleTime is 60s — override so DGI poll refetches actually hit the API every tick. */
    staleTime: 0,
    /** Without this, refetchInterval pauses when the tab is in the background (user waits, switches tab, returns — stale UI). */
    refetchIntervalInBackground: true,
    refetchInterval: (query) => {
      const inv = query.state.data as InvoiceDetailEnriched | undefined;
      return shouldPollInvoiceDetail(inv) ? 5_000 : false;
    },
  });

  const { data: pendingRecurring } = useQuery({
    queryKey: recurringInvoiceKeys.pendingForInvoice(id ?? ""),
    queryFn: () => getPendingRecurringForInvoice(id!),
    enabled:
      Boolean(id) &&
      invoice != null &&
      invoice.status === "draft",
  });

  const confirmRecurringMut = useMutation({
    mutationFn: (occurrenceId: string) => confirmRecurringOccurrence(occurrenceId),
    onSuccess: async () => {
      toast.success(t("recurringInvoices.detail.confirmedToast"));
      await queryClient.invalidateQueries({
        queryKey: recurringInvoiceKeys.pendingForInvoice(id!),
      });
      await queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id!) });
    },
    onError: () => {
      toast.error(t("recurringInvoices.detail.confirmError"));
    },
  });

  const dismissRejectionMut = useMutation({
    mutationFn: () => dismissInvoiceRejection(id!),
    onSuccess: async () => {
      toast.success(
        t("invoices.detail.dismissRejectionToast", "Alerta descartada."),
      );
      // Refresh the detail (to flip rejectionDismissedAt) and any list
      // that surfaces the rechazo count.
      await queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id!) });
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      await queryClient.invalidateQueries({ queryKey: ["accountant"] });
    },
    onError: () => {
      toast.error(
        t("invoices.detail.dismissRejectionError", "No se pudo descartar la alerta."),
      );
    },
  });

  const referencedInvoiceId =
    invoice && (isCreditNote(invoice.cfeType) || isDebitNote(invoice.cfeType))
      ? invoice.referencedInvoiceId ?? undefined
      : undefined;

  const { data: referencedInvoice } = useQuery({
    queryKey: invoiceKeys.detail(referencedInvoiceId ?? ""),
    queryFn: () => getInvoice(referencedInvoiceId!),
    enabled: Boolean(referencedInvoiceId),
  });

  useEffect(() => {
    if (isError && !draftDeletedRef.current) {
      toast.error(
        t("invoices.detail.error", "Unable to load invoice. The API may not be available yet.")
      );
    }
  }, [isError, t]);

  /** Same route component instance is kept when navigating /invoices/:id → /invoices/:otherId (e.g. reissue). */
  useEffect(() => {
    setReissueConfirmOpen(false);
    previousInvoiceStatusRef.current = undefined;
  }, [id]);

  useEffect(() => {
    if (!invoice || !id) return;
    const prev = previousInvoiceStatusRef.current;
    const invDgi = (invoice as unknown as Record<string, unknown>).dgiStatus as string | undefined;
    if (prev !== undefined && prev !== "sent" && invDgi === "confirmed") {
      toast.success(t("invoices.detail.acceptedByDgiToast"), {
        id: `invoice-accepted-${id}`,
      });
    }
    previousInvoiceStatusRef.current = invoice.status;
  }, [id, invoice, t]);

  const handleDownloadPdf = async (): Promise<void> => {
    if (!id) return;
    setPdfLoading(true);
    try {
      await downloadInvoicePdf(id);
    } catch {
      toast.error(t("invoices.detail.downloadPdfError", "Could not download PDF."));
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadDeelPdf = async (): Promise<void> => {
    if (!id) return;
    setDeelPdfLoading(true);
    try {
      await downloadDeelInvoicePdf(id);
    } catch {
      toast.error(
        t("invoices.detail.downloadDeelPdfError", "No pudimos descargar el PDF de Deel."),
      );
    } finally {
      setDeelPdfLoading(false);
    }
  };

  const handleDownloadXml = async (): Promise<void> => {
    if (!id) return;
    setXmlLoading(true);
    try {
      await downloadInvoiceXml(id);
    } catch {
      toast.error(t("invoices.detail.downloadXmlUnavailable", "Could not download XML."));
    } finally {
      setXmlLoading(false);
    }
  };

  const handleSendEmail = async (): Promise<void> => {
    if (!id) return;
    setEmailSending(true);
    try {
      await sendInvoiceEmail(id);
      toast.success(t("invoices.detail.emailSentSuccess", "Email sent to client."));
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
    } catch {
      toast.error(t("invoices.detail.emailSendError", "Could not send email. Check client email settings."));
    } finally {
      setEmailSending(false);
    }
  };

  const handleSendDraft = async (): Promise<void> => {
    if (!id) return;
    setSendingDraft(true);
    try {
      const sent = await sendDraftInvoice(id);
      toast.success(t("invoices.detail.draftSent", "Invoice sent to DGI."));
      navigate(`/invoices/${sent.id}`, { replace: true });
    } catch {
      toast.error(t("invoices.detail.draftSendError", "Could not send invoice."));
    } finally {
      setSendingDraft(false);
    }
  };

  const handleDeleteDraft = async (): Promise<void> => {
    if (!id) return;
    setDeletingDraft(true);
    try {
      await deleteDraftInvoice(id);
      draftDeletedRef.current = true;
      queryClient.removeQueries({ queryKey: invoiceKeys.detail(id) });
      await queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      toast.success(t("invoices.detail.draftDeleted", "Draft deleted."));
      navigate("/invoices", { replace: true });
    } catch {
      toast.error(t("invoices.detail.draftDeleteError", "Could not delete draft."));
    } finally {
      setDeletingDraft(false);
      setDeleteConfirmOpen(false);
    }
  };

  const handleReschedule = async (sendAtIso: string): Promise<void> => {
    if (!id) return;
    setRescheduling(true);
    try {
      await scheduleInvoiceSend(id, sendAtIso);
      setRescheduleDialogOpen(false);
      toast.success(t("invoices.schedule.scheduled", "Comprobante programado"));
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
    } catch {
      toast.error(t("invoices.schedule.rescheduleError", "No se pudo programar el envío."));
    } finally {
      setRescheduling(false);
    }
  };

  const handleCancelSchedule = async (): Promise<void> => {
    if (!id) return;
    setCancellingSchedule(true);
    try {
      await cancelScheduledInvoiceSend(id);
      setCancelScheduleConfirmOpen(false);
      toast.success(t("invoices.schedule.cancelledToast", "Programación cancelada"));
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
    } catch {
      toast.error(t("invoices.schedule.cancelError", "No se pudo cancelar la programación."));
    } finally {
      setCancellingSchedule(false);
    }
  };

  if (!id) {
    return (
      <div className="fx-content">
        <PageHeader title={t("invoices.detail.invalidId", "Invalid invoice")} />
      </div>
    );
  }

  if (isLoading) {
    return <LoadingPage />;
  }

  if (isError || !invoice) {
    return (
      <div className="fx-content">
        <PageHeader title={t("invoices.detail.title", "Invoice")} />
        <p className="mt-4 text-sm" style={{ color: "var(--fx-ink-700)" }}>
          {t("invoices.detail.notFound", "Not found.")}
        </p>
      </div>
    );
  }

  const invDgiStatus = (invoice as unknown as Record<string, unknown>).dgiStatus as string | undefined;
  // Structured DGI rejection detail (code + message). When present we render a
  // single rich rejection alert below and suppress the header's plain
  // "Rechazada" banner, so there's one error box instead of two.
  const dgiRejection = (invoice as unknown as Record<string, unknown>).dgiRejection as
    | { code?: string; description?: string; detail?: string | null }
    | undefined;
  const showNotesSection = isAcceptedBase(invoice.cfeType, invoice.status, invDgiStatus);
  const showAccountingSummary = isAcceptedBaseWithBalance(invoice);
  const canIssueCreditNoteFlag = Boolean(invoice.canIssueCreditNote);
  const canIssueDebitNoteFlag = Boolean(invoice.canIssueDebitNote);
  const canCredit = canIssueCreditNoteFlag && !invoicingBlocked;
  const canDebit = canIssueDebitNoteFlag && !invoicingBlocked;
  const currency = invoice.currency ?? "UYU";
  const isExport = isExportInvoiceType(invoice.cfeType);
  const receiverRut = invoice.receiverSnapshot?.rut;
  const receiverRutOrDoc = invoice.receiverSnapshot?.docRecepExt?.trim()
    ? invoice.receiverSnapshot.docRecepExt
    : receiverRut && !isConsumerFinalReceiver(receiverRut)
      ? receiverRut
      : null;
  const isConsumerFinal = isConsumerFinalReceiver(invoice.receiverSnapshot?.rut) && !invoice.customerId;
  // A consumidor final can still be named — see resolveConsumerFinalName.
  const consumerFinalName = isConsumerFinal
    ? resolveConsumerFinalName(invoice.receiverSnapshot?.name)
    : null;

  const pageCardClass =
    "shadow-[0_1px_2px_rgba(0,0,0,0.04)] dark:shadow-sm";

  return (
    <div className="min-h-full bg-[var(--fx-paper)] dark:bg-[var(--dk-bg)]">
      <div className="fx-content space-y-4">
        <InvoiceDetailHeader
          invoice={{
            id: invoice.id,
            status: invoice.status,
            cfeType: invoice.cfeType,
            issueDate: invoice.issueDate,
            internalNumber: invoice.internalNumber,
            currency: invoice.currency,
            totals: invoice.totals,
            scheduledSendAt: invoice.scheduledSendAt,
            brandName: invoice.brandName ?? null,
          }}
          formatMoney={formatMoney}
          onBack={() => window.history.back()}
          onDownloadPdf={handleDownloadPdf}
          pdfLoading={pdfLoading}
          xmlDownloadAvailable={invoice.status === "sent" && (invoice as unknown as Record<string, unknown>).dgiStatus === "confirmed"}
          onDownloadXml={handleDownloadXml}
          xmlLoading={xmlLoading}
          deelPdfAvailable={
            (invoice as unknown as Record<string, unknown>).createdVia === "deel" &&
            Boolean((invoice as unknown as Record<string, unknown>).deelInvoiceId)
          }
          onDownloadDeelPdf={handleDownloadDeelPdf}
          deelPdfLoading={deelPdfLoading}
          dgiStatus={(invoice as unknown as Record<string, unknown>).dgiStatus as string | undefined}
          hideRejectionBanner={Boolean(dgiRejection)}
          onReissue={
            (invoice as unknown as Record<string, unknown>).dgiStatus === "rejected" && !invoicingBlocked
              ? () => {
                  setReissueConfirmOpen(true);
                }
              : undefined
          }
          reissueLoading={false}
          onDismissRejection={
            (invoice as unknown as Record<string, unknown>).dgiStatus === "rejected" &&
            (invoice as unknown as Record<string, unknown>).rejectionDismissedAt == null
              ? () => dismissRejectionMut.mutate()
              : undefined
          }
          dismissingRejection={dismissRejectionMut.isPending}
          onReplicate={
            invoice.status === "sent" &&
            invDgiStatus === "confirmed" &&
            !isCreditNote(invoice.cfeType ?? "") &&
            !isDebitNote(invoice.cfeType ?? "") &&
            !invoicingBlocked
              ? () =>
                  navigate("/invoices/new", { state: { reissueFromId: invoice.id } })
              : undefined
          }
          onSendEmail={
            invoice.status !== "draft" &&
            invoice.customerId &&
            !(invoice as unknown as Record<string, unknown>).clientInvoiceEmailSentAt
              ? handleSendEmail
              : undefined
          }
          emailSending={emailSending}
          emailAlreadySent={false}
          onSendDraft={invoice.status === "draft" ? handleSendDraft : undefined}
          sendDraftLoading={sendingDraft}
          onEditDraft={
            invoice.status === "draft"
              ? () =>
                  navigate(`/invoices/new?draftId=${encodeURIComponent(invoice.id)}`)
              : undefined
          }
          onDeleteDraft={invoice.status === "draft" ? () => setDeleteConfirmOpen(true) : undefined}
          deleteDraftLoading={deletingDraft}
          onScheduleSend={
            invoice.status === "draft" ? () => setRescheduleDialogOpen(true) : undefined
          }
        />

        {(() => {
          const rej = dgiRejection;
          if (!rej) return null;
          const code = (rej.code ?? "").toUpperCase();
          const message = rej.detail || rej.description || "";
          // E12 = referenced CFE not found in DGI's records → steer to global ref.
          const isReferenceError =
            code === "E12" ||
            /zona de referencia/i.test(rej.description ?? "") ||
            /zona de referencia/i.test(rej.detail ?? "");
          const isCreditOrDebit =
            isCreditNote(invoice.cfeType ?? "") || isDebitNote(invoice.cfeType ?? "");
          // Single merged rejection alert: leads with the "Rechazada" status
          // (this replaces the header's plain status banner, suppressed above)
          // and carries the DGI error code + message + any actionable hint.
          return (
            <Alert variant="destructive" className={pageCardClass}>
              <XCircle className="size-4 shrink-0" />
              <AlertDescription>
                <span className="font-medium">
                  {t("invoices.list.statusLabel.rejected", "Rechazada")}
                  {" · "}
                  {t("invoices.detail.dgiRejectionTitle", "Error DGI")}
                  {code ? ` · ${code}` : ""}
                </span>
                {message && (
                  <span className="mt-0.5 block text-sm">{message}</span>
                )}
                {isReferenceError && isCreditOrDebit && (
                  <span className="mt-1 block text-sm">
                    {t(
                      "invoices.detail.dgiRejectionE12Hint",
                      "La DGI no pudo identificar el comprobante de referencia. Volvé a emitir la nota marcando “Referencia global”.",
                    )}
                  </span>
                )}
              </AlertDescription>
            </Alert>
          );
        })()}

        {invoice.status === "draft" && invoice.scheduledSendAt && (
          <Alert variant="info" className={`${pageCardClass} items-center`}>
            <CalendarClock className="size-4 shrink-0" />
            <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium">
                {t(
                  "invoices.schedule.scheduledFor",
                  "Programada para {{dateTime}}",
                  {
                    dateTime: formatDisplayDate(invoice.scheduledSendAt, {
                      includeTime: true,
                    }),
                  },
                )}
              </span>
              <span className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setRescheduleDialogOpen(true)}
                  disabled={cancellingSchedule}
                >
                  {t("invoices.schedule.reschedule", "Reprogramar")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCancelScheduleConfirmOpen(true)}
                  disabled={cancellingSchedule}
                >
                  {cancellingSchedule ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    t("invoices.schedule.cancelSchedule", "Cancelar programación")
                  )}
                </Button>
              </span>
            </AlertDescription>
          </Alert>
        )}

        {(invoice as unknown as Record<string, unknown>).createdVia === "deel" && (
          <Alert variant="info" className={pageCardClass}>
            <Building2 className="size-4 shrink-0" />
            <AlertDescription>
              <span className="font-medium text-foreground">
                {t("invoices.detail.deelOrigin", "Origen: Deel")}
              </span>
              {(() => {
                const r = invoice as unknown as Record<string, unknown>;
                const org = r.deelOrgName as string | undefined;
                const deelDate = r.deelIssuedAt as string | undefined;
                const parts: string[] = [];
                if (org) parts.push(t("invoices.detail.deelPayer", "Pagador: {{org}}", { org }));
                if (deelDate)
                  parts.push(
                    t("invoices.detail.deelInvoiceDate", "Factura Deel del {{date}}", {
                      date: formatDisplayDate(deelDate, { useCalendarDate: true }),
                    }),
                  );
                return parts.length ? (
                  <span className="text-muted-foreground"> · {parts.join(" · ")}</span>
                ) : null;
              })()}
            </AlertDescription>
          </Alert>
        )}

        {pendingRecurring?.occurrence ? (
          <Alert variant="info" className={pageCardClass}>
            <CalendarClock className="size-4 shrink-0" />
            <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-1">
                <p className="font-medium text-foreground">
                  {t("recurringInvoices.detail.bannerTitle")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("recurringInvoices.detail.bannerDescription", {
                    date: formatDisplayDate(pendingRecurring.occurrence.sendDate, {
                      locale: i18n.language,
                      useCalendarDate: true,
                    }),
                  })}
                </p>
              </div>
              <Button
                type="button"
                className="shrink-0 transition-colors duration-150"
                disabled={confirmRecurringMut.isPending}
                onClick={() => {
                  const occ = pendingRecurring?.occurrence;
                  if (occ) confirmRecurringMut.mutate(occ.id);
                }}
              >
                {confirmRecurringMut.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("recurringInvoices.detail.confirming")}
                  </>
                ) : (
                  t("recurringInvoices.detail.confirm", {
                    date: formatDisplayDate(pendingRecurring.occurrence.sendDate, {
                      locale: i18n.language,
                      useCalendarDate: true,
                    }),
                  })
                )}
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}

      {(isCreditNote(invoice.cfeType) || isDebitNote(invoice.cfeType)) &&
        invoice.referencedInvoiceId && (
          <Card className={pageCardClass}>
            <CardHeader>
              <CardTitle className="text-foreground">
                {t("invoices.detail.relatedInvoice.title", "Related invoice")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Alert variant="info">
                <Info className="size-4 shrink-0" />
                <AlertDescription className="space-y-1">
                  <p>
                    {isCreditNote(invoice.cfeType)
                      ? t(
                          "invoices.detail.relatedInvoice.creditNoteAdjusts",
                          "This credit note adjusts the following invoice."
                        )
                      : t(
                          "invoices.detail.relatedInvoice.debitNoteAdjusts",
                          "This debit note adjusts the following invoice."
                        )}
                  </p>
                  <p>
                    {isCreditNote(invoice.cfeType)
                      ? t(
                          "invoices.detail.creditNotePurpose",
                          "Credit notes reduce the amount of an existing invoice. They do not replace or delete it."
                        )
                      : t(
                          "invoices.detail.debitNotePurpose",
                          "Debit notes increase the amount of an existing invoice. They do not replace or delete it."
                        )}
                  </p>
                </AlertDescription>
              </Alert>
              {invoice.reference && (
                <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-muted-foreground">
                      {t("invoices.detail.relatedInvoice.invoiceNumber", "Invoice number")}
                    </dt>
                    <dd className="font-medium text-foreground">
                      {invoice.reference.referencedSeries != null && invoice.reference.referencedNumber != null
                        ? `${invoice.reference.referencedSeries}-${invoice.reference.referencedNumber}`
                        : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">
                      {t("invoices.detail.relatedInvoice.invoiceType", "Type")}
                    </dt>
                    <dd className="text-foreground">
                      {t(
                        `invoices.cfeTypes.${invoice.reference.referencedCfeType}`,
                        invoice.reference.referencedCfeType
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">
                      {t("invoices.detail.relatedInvoice.issueDate", "Issue date")}
                    </dt>
                    <dd className="text-foreground">
                      {invoice.reference.referencedIssueDate
                        ? formatDisplayDate(invoice.reference.referencedIssueDate, {
                            locale: i18n.language,
                            useCalendarDate: true,
                          })
                        : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">
                      {t("invoices.detail.relatedInvoice.originalTotal", "Original total")}
                    </dt>
                    <dd className="text-foreground">
                      {referencedInvoice?.totals?.total != null ? (
                        typeof referencedInvoice.totals.total === "object" &&
                        "amount" in referencedInvoice.totals.total
                          ? formatMoney(
                              referencedInvoice.currency,
                              (referencedInvoice.totals.total as { amount: number }).amount
                            )
                          : formatMoney(referencedInvoice.currency, referencedInvoice.totals.total as number)
                      ) : (
                        t(
                          "invoices.detail.relatedInvoice.viewInvoiceForAmount",
                          "View invoice for amount."
                        )
                      )}
                    </dd>
                  </div>
                </dl>
              )}
              <Button
                variant="outline"
                onClick={() => navigate(`/invoices/${invoice.referencedInvoiceId}`)}
              >
                {t("invoices.detail.relatedInvoice.viewInvoice", "View invoice")}
              </Button>
            </CardContent>
          </Card>
        )}

      {/* DGI rejection details are shown in the technical section at the bottom */}

      {(() => {
        const inv = invoice as unknown as Record<string, unknown>;
        const clientEmailSent = inv.clientInvoiceEmailSentAt != null;
        const envioCfeSent = inv.envioCfeSentAt != null;
        if (!clientEmailSent && !envioCfeSent) return null;

        const recipients = (inv.emailRecipients as string[] | undefined) ?? [];
        const ccRecipients = (inv.emailCcRecipients as string[] | undefined) ?? [];
        const dgiNotifEmail = inv.dgiNotificationEmail as string | undefined;

        return (
          <Collapsible
            defaultOpen={false}
            className="group rounded-lg border border-border bg-card border-l-2 border-l-blue-500"
          >
            <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 p-4 text-left transition-colors duration-150 hover:bg-muted/30 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-foreground">
                  {clientEmailSent && envioCfeSent
                    ? t("invoices.detail.emailCard.bothSent", "Invoice emailed to client and notified to DGI")
                    : clientEmailSent
                      ? t("invoices.detail.emailCard.clientSent", "Invoice emailed to client")
                      : t("invoices.detail.emailCard.dgiSent", "EnvioCFE notification sent")}
                </span>
              </div>
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="border-t border-border px-4 pb-4 pt-3 space-y-3 text-sm">
                {clientEmailSent && recipients.length > 0 && (
                  <div>
                    <p className="font-medium text-foreground flex items-center gap-1.5">
                      <Send className="h-3.5 w-3.5" />
                      {t("invoices.detail.emailCard.sentTo", "Sent to")}
                    </p>
                    <ul className="mt-1 space-y-0.5 text-muted-foreground">
                      {recipients.map((email) => (
                        <li key={email}>{email}</li>
                      ))}
                    </ul>
                    {ccRecipients.length > 0 && (
                      <>
                        <p className="mt-2 text-xs text-muted-foreground">CC:</p>
                        <ul className="space-y-0.5 text-muted-foreground">
                          {ccRecipients.map((email) => (
                            <li key={email}>{email}</li>
                          ))}
                        </ul>
                      </>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground">
                      {formatDisplayDate(inv.clientInvoiceEmailSentAt as string, {
                        locale: i18n.language,
                        useCalendarDate: true,
                      })}
                    </p>
                  </div>
                )}
                {envioCfeSent && (
                  <div>
                    <p className="font-medium text-foreground flex items-center gap-1.5">
                      <Mail className="h-3.5 w-3.5" />
                      {t("invoices.detail.emailCard.dgiNotification", "DGI notification (EnvioCFE)")}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t(
                        "invoices.detail.emailCard.dgiNotificationHelp",
                        "Inter-empresarial: el CFE firmado se envió al correo registrado en DGI del receptor (requisito DGI para B2B)."
                      )}
                    </p>
                    {dgiNotifEmail && (
                      <p className="mt-1 text-muted-foreground">{dgiNotifEmail}</p>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground">
                      {formatDisplayDate(inv.envioCfeSentAt as string, {
                        locale: i18n.language,
                        useCalendarDate: true,
                      })}
                    </p>
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        );
      })()}

      <Card className={pageCardClass}>
        <CardHeader>
          <CardTitle className="text-foreground">
            {t("invoices.detail.invoiceDetails", "Invoice details")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>
            <span className="text-muted-foreground">
              {t("invoices.detail.receiver", "Receiver")}:
            </span>{" "}
            {isConsumerFinal ? (
              <>
                {consumerFinalName ? (
                  <span className="font-semibold">{consumerFinalName}</span>
                ) : (
                  t("invoices.detail.receiverConsumerFinal", "Consumer final")
                )}
                <span className="block italic text-muted-foreground">
                  ({consumerFinalName
                    ? t(
                        "invoices.detail.receiverConsumerFinalNamedHelp",
                        "Consumer final - no tax identification required"
                      )
                    : t(
                        "invoices.detail.receiverConsumerFinalHelp",
                        "No tax identification required"
                      )}
                  )
                </span>
              </>
            ) : (
              <>
                {(() => {
                  const clientDisplay = (invoice as unknown as Record<string, unknown>).clientDisplayName as string | undefined;
                  const legalName = invoice.receiverSnapshot?.name;
                  const showDisplay = clientDisplay && legalName && clientDisplay.toUpperCase() !== legalName.toUpperCase();
                  return showDisplay
                    ? <><span className="font-semibold">{clientDisplay}</span>{" "}<span className="text-muted-foreground">({legalName})</span></>
                    : (legalName ?? "—");
                })()}
              </>
            )}
          </p>
          {!isConsumerFinal && receiverRutOrDoc && (
            <p>
              <span className="text-muted-foreground">
                {t("invoices.detail.rutOrDocument", "Tax ID (RUT) / Document")}:
              </span>{" "}
              {receiverRutOrDoc}
            </p>
          )}
          <p>
            <span className="text-muted-foreground">
              {t("invoices.detail.issueDate", "Issue date")}:
            </span>{" "}
            {invoice.issueDate
              ? formatDisplayDate(invoice.issueDate, {
                  locale: i18n.language,
                  useCalendarDate: true,
                })
              : "—"}
          </p>
          {/* Timestamps for the user's lifecycle understanding. The CFE
              `issueDate` above is the legal calendar date (no time), so we
              add precise timestamps here when available — acceptedAt for
              sent invoices, createdAt as the always-on baseline. */}
          {invoice.acceptedAt ? (
            <p>
              <span className="text-muted-foreground">
                {t("invoices.detail.acceptedAt", "Aceptada por DGI")}:
              </span>{" "}
              {formatDisplayDate(invoice.acceptedAt, {
                locale: i18n.language,
                includeTime: true,
              })}
            </p>
          ) : null}
          {invoice.createdAt ? (
            <p>
              <span className="text-muted-foreground">
                {t("invoices.detail.createdAt", "Creada")}:
              </span>{" "}
              {formatDisplayDate(invoice.createdAt, {
                locale: i18n.language,
                includeTime: true,
              })}
            </p>
          ) : null}
          <p>
            <span className="text-muted-foreground">
              {t("invoices.detail.currency", "Currency")}:
            </span>{" "}
            {currency}
          </p>
          {invoice.totals?.total && (
            <p>
              <span className="text-muted-foreground">
                {t("invoices.detail.total", "Total")}:
              </span>{" "}
              <span className="font-semibold text-foreground">
                {formatMoney(
                  invoice.totals.total.currency ?? currency,
                  invoice.totals.total.amount
                )}
              </span>
            </p>
          )}
          {invoice.exchangeRate != null && (
            <div className="mt-4 space-y-1 border-t pt-4 text-sm text-muted-foreground">
              {currency === "USD" ? (
                <p>
                  {t("invoices.detail.exchangeRateForIva", "Exchange rate: 1 USD = {{rate}} UYU (used to calculate IVA to pay)", {
                    rate: invoice.exchangeRate,
                  })}
                </p>
              ) : (
                <>
                  <p>
                    {t("invoices.detail.conversionFromUsd", "Converted from USD at 1 USD = {{rate}} UYU", {
                      rate: invoice.exchangeRate,
                    })}
                  </p>
                  {invoice.originalTotals?.total && (
                    <p>
                      {t("invoices.detail.originalTotal", "Original total: {{amount}}", {
                        amount: formatMoney(
                          invoice.originalTotals.total.currency ?? "USD",
                          invoice.originalTotals.total.amount
                        ),
                      })}
                    </p>
                  )}
                </>
              )}
            </div>
          )}
          {isExport && (
            <p className="text-muted-foreground">
              {invoice.receiverSnapshot?.countryCode && (
                <span>
                  {t("invoices.detail.country", "Country")}:{" "}
                  {invoice.receiverSnapshot.countryCode}.{" "}
                </span>
              )}
              {t("invoices.detail.exportNote", "Export invoice (no VAT).")}
            </p>
          )}
        </CardContent>
      </Card>

      {invoice.status === "draft" && invoice.customerId && (
        <InvoiceSendSettingsCard
          customerId={invoice.customerId}
          invoiceId={invoice.id}
          autoSendEmailOverride={
            (invoice as unknown as Record<string, unknown>).autoSendEmailOverride as
              | boolean
              | undefined
          }
        />
      )}

      {showAccountingSummary && (
        <FinancialSummary
          className={pageCardClass}
          currency={currency}
          originalInvoiceTotal={invoice.originalInvoiceTotal!}
          totalCredited={invoice.totalCredited ?? 0}
          totalDebited={invoice.totalDebited ?? 0}
          remainingBalance={invoice.remainingBalance!}
        />
      )}

      {showNotesSection && (
        <>
          <RelatedDocumentsList
            relatedCreditNotes={invoice.relatedCreditNotes ?? []}
            relatedDebitNotes={invoice.relatedDebitNotes ?? []}
          />
          <Card className={pageCardClass}>
            <CardHeader>
              <CardTitle className="text-foreground">
                {t("invoices.detail.adjustments", "Adjustments")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t(
                  "invoices.detail.adjustmentsCopy",
                  "Invoices cannot be edited or deleted. To return or adjust amounts, issue a return (credit note) — it is the only valid way to adjust an invoice."
                )}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="default"
                  size="sm"
                  disabled={!canCredit}
                  title={
                    invoicingBlocked && invoicingBlockedTooltipKey
                      ? t(invoicingBlockedTooltipKey)
                      : !canIssueCreditNoteFlag
                        ? t(
                            "invoices.detail.issueCreditNoteDisabled",
                            "No remaining balance available"
                          )
                        : undefined
                  }
                  onClick={() => navigate(`/invoices/${id}/credit-note`)}
                  className="transition-colors duration-150"
                >
                  <FileMinus className="mr-2 h-4 w-4" />
                  {t("invoices.detail.returnInvoice", "Return")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <Card className={pageCardClass}>
        <CardHeader>
          <CardTitle className="text-foreground">
            {t("invoices.detail.lineItems", "Line items")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {invoice.lines?.length ? (
            <>
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        {t("invoices.detail.lineItemsDescription", "Description")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("invoices.detail.lineItemsQuantity", "Quantity")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("invoices.detail.lineItemsUnitPrice", "Unit price")}
                      </TableHead>
                      <TableHead
                        className="text-right"
                        title={t("invoices.detail.lineItemsTaxesTooltip", "VAT included")}
                      >
                        {t("invoices.detail.lineItemsTaxes", "Taxes")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("invoices.detail.lineItemsLineTotal", "Line total")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoice.lines.map((line) => (
                      <TableRow key={line.lineNumber} className="even:bg-muted/30">
                        <TableCell className="whitespace-pre-line">{line.description ?? "—"}</TableCell>
                        <TableCell className="text-right">
                          {line.quantity != null
                            ? Number(line.quantity).toLocaleString("es-UY", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                              })
                            : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          {line.unitPrice
                            ? formatMoney(
                                line.unitPrice.currency ?? currency,
                                line.unitPrice.amount
                              )
                            : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          {line.tax?.amount != null
                            ? formatMoney(currency, line.tax.amount)
                            : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          {line.lineTotal
                            ? formatMoney(
                                line.lineTotal.currency ?? currency,
                                line.lineTotal.amount
                              )
                            : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <ul className="space-y-3 md:hidden">
                {invoice.lines.map((line) => (
                  <li
                    key={line.lineNumber}
                    className="rounded-md border border-border bg-background/40 p-3"
                  >
                    <p className="whitespace-pre-line text-sm font-medium text-foreground">
                      {line.description ?? "—"}
                    </p>
                    <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                      <div>
                        <dt className="text-xs text-muted-foreground">
                          {t("invoices.detail.lineItemsQuantity", "Quantity")}
                        </dt>
                        <dd className="tabular-nums text-foreground">
                          {line.quantity != null
                            ? Number(line.quantity).toLocaleString("es-UY", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                              })
                            : "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">
                          {t("invoices.detail.lineItemsUnitPrice", "Unit price")}
                        </dt>
                        <dd className="tabular-nums text-foreground">
                          {line.unitPrice
                            ? formatMoney(
                                line.unitPrice.currency ?? currency,
                                line.unitPrice.amount
                              )
                            : "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">
                          {t("invoices.detail.lineItemsTaxes", "Taxes")}
                        </dt>
                        <dd className="tabular-nums text-foreground">
                          {line.tax?.amount != null
                            ? formatMoney(currency, line.tax.amount)
                            : "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">
                          {t("invoices.detail.lineItemsLineTotal", "Line total")}
                        </dt>
                        <dd className="font-semibold tabular-nums text-foreground">
                          {line.lineTotal
                            ? formatMoney(
                                line.lineTotal.currency ?? currency,
                                line.lineTotal.amount
                              )
                            : "—"}
                        </dd>
                      </div>
                    </dl>
                  </li>
                ))}
              </ul>
              {invoice.totals && (
                <div className="mt-4 flex flex-col items-end gap-1 text-sm">
                  {invoice.totals.subtotal && (
                    <p className="text-muted-foreground">
                      {t("invoices.detail.subtotal", "Subtotal")}:{" "}
                      {formatMoney(
                        invoice.totals.subtotal.currency ?? currency,
                        invoice.totals.subtotal.amount
                      )}
                    </p>
                  )}
                  {invoice.totals.taxTotal && (
                    <p className="text-muted-foreground">
                      {t("invoices.detail.taxTotal", "Tax total (IVA)")}:{" "}
                      {formatMoney(
                        invoice.totals.taxTotal.currency ?? currency,
                        invoice.totals.taxTotal.amount
                      )}
                    </p>
                  )}
                  {invoice.totals.total && (
                    <p className="font-semibold text-foreground">
                      {t("invoices.detail.total", "Total")}:{" "}
                      {formatMoney(
                        invoice.totals.total.currency ?? currency,
                        invoice.totals.total.amount
                      )}
                    </p>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t("invoices.detail.lineItemsEmpty", "No line items.")}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Technical/fiscal block (internal id, CFE code, DGI/EnvioCFE/ack
          state) is only meaningful once the CFE exists at DGI. A draft has no
          real fiscal footprint yet, so hide it entirely for drafts. */}
      {invoice.status !== "draft" && (
      <Collapsible open={technicalOpen} onOpenChange={setTechnicalOpen} className="group">
        <Card className={pageCardClass}>
          <CollapsibleTrigger asChild>
            <CardHeader className="flex flex-row items-center justify-between gap-2 cursor-pointer transition-colors hover:bg-muted/40">
              <CardTitle className="text-muted-foreground text-sm font-medium">
                {t("invoices.detail.technicalTitle", "Technical / Fiscal Information")}
              </CardTitle>
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-2 border-t border-muted pt-4 text-sm">
              <p className="text-muted-foreground">
                {t("invoices.detail.technicalHelper", "For support and auditing.")}
              </p>
              <p>
                <span className="text-muted-foreground">
                  {t("invoices.detail.technicalInternalId", "Internal ID")}:
                </span>{" "}
                {invoice.id}
              </p>
              <p>
                <span className="text-muted-foreground">
                  {t("invoices.detail.technicalCfeType", "CFE type code")}:
                </span>{" "}
                {invoice.cfeType}
              </p>
              {invoice.cae && (
                <>
                  <p>
                    <span className="text-muted-foreground">
                      {t("invoices.detail.technicalCae", "CAE authorization")}:
                    </span>{" "}
                    {invoice.cae.id}
                  </p>
                  {invoice.cae.expiryDate && (
                    <p>
                      <span className="text-muted-foreground">
                        {t("invoices.detail.technicalCaeValidity", "CAE validity")}:
                      </span>{" "}
                      {formatDisplayDate(invoice.cae.expiryDate, {
                        locale: i18n.language,
                      })}
                    </p>
                  )}
                </>
              )}
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">
                  {t("invoices.detail.technicalDgiStatus", "DGI status")}:
                </span>
                {(() => {
                  const dgi = (invoice as unknown as Record<string, unknown>).dgiStatus as string | undefined;
                  if (dgi === "confirmed") return (
                    <StatusBadge variant="success">
                      {t("invoices.detail.dgiConfirmed", "Confirmada por DGI")}
                    </StatusBadge>
                  );
                  if (dgi === "rejected") return (
                    <StatusBadge variant="danger">
                      {t("invoices.detail.dgiRejected", "Rechazada por DGI")}
                    </StatusBadge>
                  );
                  if (dgi === "sent") return (
                    <StatusBadge variant="info">
                      {t("invoices.detail.dgiSent", "Enviada a DGI")}
                    </StatusBadge>
                  );
                  return (
                    <StatusBadge variant="warning">
                      {t("invoices.detail.dgiPending", "Pendiente")}
                    </StatusBadge>
                  );
                })()}
              </div>
              {(() => {
                const raw = invoice as unknown as Record<string, unknown>;
                const envioCfeSentAt = raw.envioCfeSentAt as string | undefined;
                const acuseReciboReceivedAt = raw.acuseReciboReceivedAt as string | undefined;
                const receptorAckCfeEstado = raw.receptorAckCfeEstado as string | undefined;
                const receptorAckCfeReceivedAt = raw.receptorAckCfeReceivedAt as string | undefined;
                const receptorAckCfeMotivos = raw.receptorAckCfeMotivos as
                  | Array<{ code: string; description: string; detail?: string }>
                  | undefined
                  | null;
                if (!envioCfeSentAt) return null;
                return (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">
                        {t("invoices.detail.technicalEnvioCfe", "EnvioCFE")}:
                      </span>
                      <StatusBadge variant="success">
                        {t("invoices.detail.envioCfeSent", "Enviado {{date}}", {
                          date: formatDisplayDate(envioCfeSentAt, { locale: i18n.language }),
                        })}
                      </StatusBadge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">
                        {t("invoices.detail.technicalAcuseRecibo", "Acuse de recibo")}:
                      </span>
                      {acuseReciboReceivedAt ? (
                        <StatusBadge variant="success">
                          {t("invoices.detail.acuseReciboReceived", "Recibido {{date}}", {
                            date: formatDisplayDate(acuseReciboReceivedAt, { locale: i18n.language }),
                          })}
                        </StatusBadge>
                      ) : (
                        <StatusBadge variant="warning">
                          {t("invoices.detail.acuseReciboPending", "Pendiente")}
                        </StatusBadge>
                      )}
                    </div>
                    {/* Receptor ACKCFE — business-layer accept/reject (DGI §XVIII, 20 días) */}
                    <div className="flex items-start gap-2">
                      <span className="text-muted-foreground">
                        {t("invoices.detail.technicalReceptorAckCfe", "Aceptación del receptor")}:
                      </span>
                      {receptorAckCfeEstado === "AE" && receptorAckCfeReceivedAt ? (
                        <StatusBadge variant="success">
                          {t("invoices.detail.receptorAckCfeAccepted", "Aceptada el {{date}}", {
                            date: formatDisplayDate(receptorAckCfeReceivedAt, { locale: i18n.language }),
                          })}
                        </StatusBadge>
                      ) : receptorAckCfeEstado === "RE" && receptorAckCfeReceivedAt ? (
                        <div className="flex flex-col gap-1">
                          <StatusBadge variant="danger">
                            {t("invoices.detail.receptorAckCfeRejected", "Rechazada el {{date}}", {
                              date: formatDisplayDate(receptorAckCfeReceivedAt, { locale: i18n.language }),
                            })}
                          </StatusBadge>
                          {receptorAckCfeMotivos && receptorAckCfeMotivos.length > 0 && (
                            <ul className="ml-1 text-xs text-muted-foreground">
                              {receptorAckCfeMotivos.map((m, i) => (
                                <li key={i}>
                                  <code className="text-[10px]">{m.code}</code> — {m.description}
                                  {m.detail ? <span className="text-muted-foreground/70"> ({m.detail})</span> : null}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ) : (
                        <StatusBadge variant="warning">
                          {t("invoices.detail.receptorAckCfePending", "Pendiente (plazo 20 días)")}
                        </StatusBadge>
                      )}
                    </div>
                  </>
                );
              })()}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
      )}
      </div>

      <ConfirmationDialog
        open={reissueConfirmOpen}
        onOpenChange={setReissueConfirmOpen}
        title={t(`invoices.reissue.confirmTitle_${invoice.cfeType}`, t("invoices.reissue.confirmTitle"))}
        description={t(`invoices.reissue.confirmDescription_${invoice.cfeType}`, t("invoices.reissue.confirmDescription"))}
        confirmText={t(`invoices.reissue.confirmButton_${invoice.cfeType}`, t("invoices.reissue.confirmButton"))}
        cancelText={t("pages.common.cancel")}
        onConfirm={() => {
          setReissueConfirmOpen(false);
          reissueRejected({
            invoiceId: invoice.id,
            cfeType: invoice.cfeType,
            referencedInvoiceId: invoice.referencedInvoiceId ?? undefined,
          });
        }}
      />

      <ConfirmationDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={t("invoices.detail.deleteDraftConfirmTitle", "Delete draft?")}
        description={t("invoices.detail.deleteDraftConfirmDescription", "This draft will be permanently deleted. This action cannot be undone.")}
        confirmText={t("invoices.detail.deleteDraftConfirmButton", "Delete")}
        cancelText={t("pages.common.cancel")}
        variant="destructive"
        isLoading={deletingDraft}
        onConfirm={() => void handleDeleteDraft()}
      />

      <ConfirmationDialog
        open={cancelScheduleConfirmOpen}
        onOpenChange={setCancelScheduleConfirmOpen}
        title={t("invoices.schedule.cancelConfirmTitle", "¿Cancelar la programación?")}
        description={t(
          "invoices.schedule.cancelConfirmDescription",
          "El comprobante quedará como borrador sin enviarse automáticamente. Podés programarlo de nuevo o enviarlo manualmente cuando quieras."
        )}
        confirmText={t("invoices.schedule.cancelConfirmButton", "Cancelar programación")}
        cancelText={t("pages.common.cancel")}
        isLoading={cancellingSchedule}
        onConfirm={() => void handleCancelSchedule()}
      />

      <ScheduleSendDialog
        open={rescheduleDialogOpen}
        onOpenChange={setRescheduleDialogOpen}
        submitting={rescheduling}
        onConfirm={handleReschedule}
        isReschedule={Boolean(invoice.scheduledSendAt)}
        initialDate={
          invoice.scheduledSendAt
            ? toUruguayDateTimeParts(invoice.scheduledSendAt).date
            : undefined
        }
        initialTime={
          invoice.scheduledSendAt
            ? toUruguayDateTimeParts(invoice.scheduledSendAt).time
            : undefined
        }
      />
    </div>
  );
}
